import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../../context/ToastContext'
import { useTextGenerationModels } from '../../../../hooks/queries/useTextGenerationModels'

import { APIKeyStatus } from '../../../../models/storeCustomerVendorAPIKeys.models'

import { saveAIConfigPreferences } from '../../../../services/saveAIConfigService'
import styles from './AIVendorWidget.module.scss'
import { Tooltip } from '@genflowly/component-library'
import { Icon } from '@genflowly/component-library'
import { ICONS } from '../../../../constants/icons'
import { PillButton } from '@genflowly/component-library'
import {
    VendorBrandName,
    CollapsedVendorBrandName
} from '../../../common/BrandName/BrandName'
import { useSidebarWidget } from '../../../../hooks/useSidebarWidget'
import { getThinkingLevelInfo } from '../../../../utils/thinkingLevels'
import SidebarWidgetContainer from '../../../common/SidebarWidgetContainer/SidebarWidgetContainer'

export default function AIVendorWidget({ 'data-testid': testId }: { 'data-testid'?: string } = {}): React.ReactElement {
    const { t } = useTranslation()
    const { ai, auth } = useSidebarWidget()
    const { config, setSelectedModelForAIVendor, setThinkingLevelForAIVendor } = ai
    const { getAccessTokenSilently, customerId, isAuthenticated, aiVendorAPIKeyStatusList } = auth
    const { showToast } = useToast()

    // Only vendors with a working key are offered — the key-status list
    // carries every vendor the customer configured (legacy three plus any
    // BYOK long-tail vendor), and keyless ones are filtered out entirely.
    const vendors = React.useMemo<string[]>(() => (
        aiVendorAPIKeyStatusList
            .filter(s => s.status === APIKeyStatus.VALID || s.isAPIKeyValid)
            .map(s => s.aiVendor)
    ), [aiVendorAPIKeyStatusList])

    // Shared query (dedups with VendorModelSelect/AISettings): paints from the
    // last known list, skips the round trip entirely when no vendor has a key.
    const { data: modelsData, clearAndRefetch } = useTextGenerationModels(vendors)
    const aiVendorModels = modelsData ?? []
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const isAIVendorModelsPopulated = (aiVendor: string) =>
        config.some(c => c.aiVendor === aiVendor && c.model)

    const getSelectedModel = (aiVendor: string) =>
        config.find(c => c.aiVendor === aiVendor)?.model ?? ''

    const getThinkingLevel = (aiVendor: string): string =>
        config.find(c => c.aiVendor === aiVendor)?.thinkingLevel ?? ''

    const isVendorEnabled = (vendor: string) => {
        const status = aiVendorAPIKeyStatusList.find(s => s.aiVendor === vendor)
        return status?.status === APIKeyStatus.VALID
    }

    // Backend preference → config sync now lives in AIContext, so model
    // selections exist even before this widget first mounts (mobile).

    const handleRefresh = async () => {
        if (!customerId) return

        try {
            setIsRefreshing(true)
            await clearAndRefetch()
        } finally {
            setIsRefreshing(false)
        }
    }

    const handleSave = async () => {
        if (!isAuthenticated || !customerId) return

        try {
            setIsSaving(true)
            const token = await getAccessTokenSilently()

            await saveAIConfigPreferences(token, {
                aiConfigPreferences: {
                    preferredModels: config.filter(c => c.model && c.model.trim() !== '')
                }
            })
            showToast(t('sidebar.aiVendorWidget.preferencesSavedSuccessfully'))
        } catch (err: any) {
            console.error('Failed to save preferences:', err)
            showToast(t('sidebar.aiVendorWidget.failedToSavePreferences', { message: err.message }))
        } finally {
            setIsSaving(false)
        }
    }


    const getModelsForVendor = (vendor: string): string[] =>
        aiVendorModels.find(v => v.aiVendor === vendor)?.modelsList ?? []

    const hasAnyValidApiKey = vendors.some(isVendorEnabled)

    // Collapsed Content
    const headerCollapsed = (
        <Tooltip content={t('sidebar.aiVendorWidget.aiModelsTooltip')} position="left">
            <div className={styles.buttonWrapper}>
                <div className={styles.iconButton} style={{ border: 'none', background: 'transparent', cursor: 'default' }}>
                    <Icon src={ICONS.MODELS} size={24} />
                </div>
            </div>
        </Tooltip>
    )

    const collapsedContent = (
        <>
            {vendors.map(vendor => {
                const isActive = isAIVendorModelsPopulated(vendor)
                const isEnabled = isVendorEnabled(vendor)
                const models = getModelsForVendor(vendor)

                const tooltipContent = !isEnabled ? t('sidebar.aiVendorWidget.apiKeyNotSet') : vendor

                return (
                    <Tooltip key={vendor} content={tooltipContent} position="left">
                        <div className={styles.buttonWrapper}>
                            <button
                                type="button"
                                className={`${styles.iconButton} ${isActive ? styles.active : ''}`}
                                onClick={() => {
                                    if (!isEnabled) return
                                    if (isActive) {
                                        setSelectedModelForAIVendor(vendor, '')
                                    } else {
                                        const firstModel = models[0]
                                        if (firstModel)
                                            setSelectedModelForAIVendor(vendor, firstModel)
                                    }
                                }}
                                disabled={!isEnabled}
                            >
                                <CollapsedVendorBrandName vendorId={vendor} size={20} />
                            </button>
                        </div>
                    </Tooltip>
                )
            })}
        </>
    )

    // Expanded Header
    const header = (
        <div className={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                <Icon src={ICONS.MODELS} size={20} />
                <h3 className={styles.title}>{t('sidebar.aiVendorWidget.title')}</h3>
            </div>
            <div className={styles.actions}>
                <Tooltip content={!hasAnyValidApiKey ? t('sidebar.aiVendorWidget.noApiKeysSet') : t('sidebar.aiVendorWidget.refreshModels')}>
                    <button
                        className={styles.iconActionButton}
                        onClick={handleRefresh}
                        disabled={isRefreshing || !hasAnyValidApiKey}
                        aria-label={t('sidebar.aiVendorWidget.refreshModels')}
                        data-testid="refresh-models-button"
                    >
                        <Icon
                            src={ICONS.REFRESH_ICON}
                            size={20}
                            className={isRefreshing ? styles.spinning : ''}
                        />
                    </button>
                </Tooltip>

                <Tooltip content={!hasAnyValidApiKey ? t('sidebar.aiVendorWidget.noApiKeysSet') : t('sidebar.aiVendorWidget.setAsDefault')}>
                    <button
                        className={styles.iconActionButton}
                        onClick={handleSave}
                        disabled={isSaving || !hasAnyValidApiKey}
                        aria-label={t('sidebar.aiVendorWidget.setAsDefault')}
                    >
                        <Icon src={ICONS.SAVE_ICON} size={20} />
                    </button>
                </Tooltip>
            </div>
        </div>
    )

    return (
        <SidebarWidgetContainer
            header={header}
            headerCollapsed={headerCollapsed}
            collapsedContent={collapsedContent}
            data-testid={testId || "ai-vendor-widget"}
        >
            <div className={styles.vendorWidget}>
                {vendors.length === 0 && (
                    <span className={styles.errorText}>{t('sidebar.aiVendorWidget.noApiKeysSet')}</span>
                )}
                {vendors.map(vendor => {
                    const isActive = isAIVendorModelsPopulated(vendor)
                    const selectedModel = getSelectedModel(vendor)
                    const models = getModelsForVendor(vendor)
                    const isEnabled = isVendorEnabled(vendor)

                    const BrandComponent: React.ReactNode = <VendorBrandName vendorId={vendor} as="span" />

                    return (
                        <div key={vendor} className={styles.vendorBlock}>
                            <PillButton
                                label={BrandComponent}
                                onClick={() => {
                                    if (!isEnabled) return
                                    if (isActive) {
                                        setSelectedModelForAIVendor(vendor, '')
                                    } else {
                                        const firstModel = models[0]
                                        if (firstModel)
                                            setSelectedModelForAIVendor(vendor, firstModel)
                                    }
                                }}
                                disabled={!isEnabled}
                                variant={isActive ? 'primary' : 'secondary'}
                                data-testid={`vendor-toggle-${vendor}`}
                            />

                            {!isEnabled && (
                                <span className={styles.errorText}>
                                    {aiVendorAPIKeyStatusList.find(s => s.aiVendor === vendor)?.status || APIKeyStatus.NOT_SET}
                                </span>
                            )}

                            {isActive && isEnabled && models.length > 0 && (
                                <select
                                    value={selectedModel}
                                    onChange={e =>
                                        setSelectedModelForAIVendor(vendor, e.target.value)
                                    }
                                    className={styles.modelSelect}
                                    data-testid={`model-select-${vendor}`}
                                >
                                    {models.map(model => (
                                        <option key={model} value={model}>
                                            {model}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {/* Reasoning effort for this vendor's responses; rides the
                                request's aiConfig and overrides the agent's node-level
                                thinking_level on the response generator. */}
                            {isActive && isEnabled && selectedModel &&
                                getThinkingLevelInfo(selectedModel).options.length > 0 && (
                                <select
                                    value={getThinkingLevel(vendor)}
                                    onChange={e =>
                                        setThinkingLevelForAIVendor(vendor, e.target.value)
                                    }
                                    className={styles.modelSelect}
                                    aria-label={t('sidebar.aiVendorWidget.reasoning')}
                                    data-testid={`thinking-select-${vendor}`}
                                >
                                    <option value="">
                                        {t('sidebar.aiVendorWidget.reasoningDefault')}
                                    </option>
                                    {getThinkingLevelInfo(selectedModel).options.map(o => (
                                        <option key={o.value} value={o.value}>
                                            {t(`agentsPage.nodeConfigEditor.thinkingLevel_${o.value}`)}
                                            {o.native ? ` — ${o.native}` : ''}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {isActive && isEnabled && models.length === 0 && !isRefreshing && (
                                <span className={styles.errorText}>{t('sidebar.aiVendorWidget.noModelsAvailable')}</span>
                            )}
                        </div>
                    )
                })}
            </div>
        </SidebarWidgetContainer>
    )
}
